import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { MapPin, Loader2, Save, Shield, UserCheck, AlertTriangle } from 'lucide-react';

export default function AdminAssign() {
  const [users, setUsers] = useState([]);
  const [precincts, setPrecincts] = useState([]);
  const [owners, setOwners] = useState({ team_leaders: {}, surveyors: {} });
  const [selectedUser, setSelectedUser] = useState('');
  const [assignedIds, setAssignedIds] = useState([]);
  const [singleSurveyorMode, setSingleSurveyorMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [u, p, o] = await Promise.all([
        API.get('/admin/users'),
        API.get('/admin/precincts'),
        API.get('/admin/precinct-owners'),
      ]);
      setUsers(u.data.filter(x => x.role !== 'admin'));
      setPrecincts(p.data);
      setOwners(o.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const loadAssignments = useCallback(async (userId) => {
    if (!userId) { setAssignedIds([]); return; }
    try {
      const { data } = await API.get(`/admin/assignments/${userId}`);
      setAssignedIds(data);
    } catch { setAssignedIds([]); }
  }, []);

  useEffect(() => {
    if (selectedUser) loadAssignments(selectedUser);
  }, [selectedUser, loadAssignments]);

  const togglePrecinct = (pid) => {
    setAssignedIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    const user = users.find(u => u.id === parseInt(selectedUser));
    try {
      if (user?.role === 'team_leader') {
        await API.post('/admin/assign-team-leader', { user_id: parseInt(selectedUser), ids: assignedIds });
      } else {
        await API.post('/admin/assign-surveyor', { user_id: parseInt(selectedUser), ids: assignedIds, single_mode: singleSurveyorMode });
      }
      await loadAll();
      alert('Precincts assigned successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Assignment failed');
    }
    setSaving(false);
  };

  const selectedUserObj = users.find(u => u.id === parseInt(selectedUser));
  const teamLeaders = users.filter(u => u.role === 'team_leader');
  const surveyors = users.filter(u => u.role === 'surveyor');

  if (loading) return <div className="flex justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-assign-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Assign Precincts</h1>
        <p className="text-slate-500 text-sm mt-1">Assign precincts to Team Leaders (1 per precinct) and Surveyors</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Selection */}
        <Card className="border-slate-200 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Select User</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {teamLeaders.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Team Leaders
                </p>
                <div className="space-y-1">
                  {teamLeaders.map(u => (
                    <button key={u.id} data-testid={`select-user-${u.id}`} onClick={() => setSelectedUser(String(u.id))}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${selectedUser === String(u.id) ? 'bg-blue-50 border border-blue-300 text-blue-900' : 'hover:bg-slate-50 border border-transparent'}`}>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-2">@{u.username}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {surveyors.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> Surveyors
                </p>
                <div className="space-y-1">
                  {surveyors.map(u => (
                    <button key={u.id} data-testid={`select-user-${u.id}`} onClick={() => setSelectedUser(String(u.id))}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${selectedUser === String(u.id) ? 'bg-blue-50 border border-blue-300 text-blue-900' : 'hover:bg-slate-50 border border-transparent'}`}>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-2">@{u.username}</span>
                      {!u.is_active && <Badge className="ml-2 text-xs bg-red-100 text-red-800">Inactive</Badge>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Precinct Assignment */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-heading text-lg">
                {selectedUserObj ? (
                  <>
                    <MapPin className="w-5 h-5 inline mr-2 text-blue-500" />
                    Precincts for {selectedUserObj.name}
                    <Badge className="ml-2 text-xs bg-slate-100 text-slate-600">{selectedUserObj.role.replace('_', ' ')}</Badge>
                  </>
                ) : 'Select a user to assign precincts'}
              </CardTitle>
              <div className="flex items-center gap-3">
                {selectedUserObj?.role === 'surveyor' && (
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                    <Switch
                      data-testid="single-surveyor-toggle"
                      checked={singleSurveyorMode}
                      onCheckedChange={setSingleSurveyorMode}
                    />
                    <Label className="text-xs text-slate-600 cursor-pointer whitespace-nowrap">
                      {singleSurveyorMode ? 'Single surveyor/precinct' : 'Multiple surveyors/precinct'}
                    </Label>
                  </div>
                )}
                {selectedUser && (
                  <Button data-testid="save-assignments-btn" onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800" size="sm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
                  </Button>
                )}
              </div>
            </div>
            {selectedUser && (
              <p className="text-xs text-slate-500 mt-1">{assignedIds.length} of {precincts.length} precincts selected</p>
            )}
          </CardHeader>
          <CardContent>
            {!selectedUser ? (
              <div className="text-center py-12 text-slate-400">
                <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a Team Leader or Surveyor from the left panel</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto pr-2">
                {precincts.map(p => {
                  const isChecked = assignedIds.includes(p.id);
                  const tlOwner = owners.team_leaders[p.id];
                  const surveyorOwners = owners.surveyors[p.id] || [];
                  const isTLMode = selectedUserObj?.role === 'team_leader';
                  const isOwnedByOtherTL = isTLMode && tlOwner && tlOwner.id !== parseInt(selectedUser);
                  return (
                    <label key={p.id} data-testid={`precinct-checkbox-${p.id}`}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}>
                      <Checkbox checked={isChecked} onCheckedChange={() => togglePrecinct(p.id)} className="mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-medium text-slate-900">{p.precinct_number}</p>
                        {p.city_municipality && <p className="text-xs text-slate-500">{p.city_municipality}</p>}
                        {tlOwner && (
                          <p className={`text-xs mt-0.5 ${isOwnedByOtherTL ? 'text-amber-600' : 'text-blue-600'}`}>
                            {isOwnedByOtherTL && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                            TL: {tlOwner.name}
                          </p>
                        )}
                        {surveyorOwners.length > 0 && (
                          <p className="text-xs text-emerald-600 mt-0.5">
                            {surveyorOwners.map(s => s.name).join(', ')}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
